import {
  ApplicationConfig,
  inject,
  LOCALE_ID,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { registerLocaleData } from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import { catchError, of } from 'rxjs';
import { Observable } from 'rxjs';
import { provideTransloco } from '@jsverse/transloco';
import { provideHotToastConfig } from '@ngxpert/hot-toast';
import { environment } from '../environments/environment';

import { routes } from './app.routes';
import { apiCredentialsInterceptor } from './shared/interceptors/api-credentials.interceptor';
import { notFoundInterceptor } from './shared/interceptors/not-found.interceptor';
import { RefreshTokenInterceptor } from './shared/interceptors/refresh-token.interceptor';
import { AuthenticationService } from './features/auth/services/authentication.service';
import { TranslocoHttpLoader } from './transloco/transloco-loader';

// Angular's date/number pipes read LOCALE_ID, which Transloco does not drive.
registerLocaleData(localeFr);

/**
 * Initialize authentication on app startup
 * Verifies user session with backend
 * Gracefully handles unauthenticated state
 */
function initializeAuth(): Observable<unknown> {
  const authService = inject(AuthenticationService);

  return authService.verifyAuth().pipe(
    catchError(() => {
      return of(null);
    }),
  );
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    { provide: LOCALE_ID, useValue: 'fr' },
    provideRouter(routes, withComponentInputBinding()),
    provideHttpClient(withInterceptors([apiCredentialsInterceptor, notFoundInterceptor]), withInterceptorsFromDi()),
    provideTransloco({
      config: {
        availableLangs: ['fr', 'en'],
        defaultLang: 'fr',
        fallbackLang: 'fr',
        reRenderOnLangChange: true,
        prodMode: environment.production,
      },
      loader: TranslocoHttpLoader,
    }),
    provideHotToastConfig({
      position: 'top-right',
      dismissible: true,
      duration: 3000,
    }),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RefreshTokenInterceptor,
      multi: true,
    },
    provideAppInitializer(initializeAuth),
  ],
};
